import {
	FIELD_TEXT,
	FIELD_TEXTAREA,
	FIELD_TITLE,
	FIELD_DESCRIPTION,
	FIELDS_START_END_INPUTS,
	FIELDS_INPUT_DECISION_RADIOGROUP
} from '../constants/fields';

/**
 * FormHelper class for processing form data and extracting values based on FormTemplate
 * Provides comprehensive form processing utilities for any form type
 */
class FormHelper {

	/**
	 * Generic helper to retrieve field value from fieldResults using FormTemplate.Fields
	 * @param {Array} fieldResults - Array of field result objects
	 * @param {Array} templateFields - Array of field definitions from FormTemplate.Fields
	 * @returns {Object} - Object mapping field names to extracted values
	 */
	static extractFieldsFromResults(fieldResults, templateFields) {
		if (!Array.isArray(fieldResults) || !Array.isArray(templateFields)) return {};
		const result = {};

		templateFields.forEach(fieldDef => {
			const { name, type } = fieldDef;
			const fieldResult = fieldResults.find(f => f.fieldName === name);
			if (!fieldResult) return;

			let value = fieldResult.value;
			// Extraction logic based on type
			switch (type) {
				case FIELDS_START_END_INPUTS:
					// Date range input - parse JSON
					try {
						value = JSON.parse(value);
					} catch (e) { }
					break;
				case FIELD_TEXTAREA:
					// Textarea content - strip HTML tags
					value = FormHelper.stripHtmlTagsStatic(value);
					break;
				case FIELD_TEXT:
					// Regular text input - use as is
					break;
				case FIELDS_INPUT_DECISION_RADIOGROUP:
					// Radio group decision - use as is
					break;
				case FIELD_TITLE:
				case FIELD_DESCRIPTION:
					// Title and description - use as is
					break;
				// Add more types as needed
				default:
					// Use raw value for other types
					break;
			}
			result[name] = value;
		});
		return result;
	}

	/**
	 * Static HTML tag stripper for use in static helpers
	 */
	static stripHtmlTagsStatic(html) {
		if (!html) return '';
		return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
	}

	/**
	 * Validate form data against template requirements
	 * @param {Object} formData - The processed form data
	 * @param {Array} templateFields - Array of field definitions
	 * @returns {Object} - Validation result with errors array
	 */
	static validateFormData(formData, templateFields) {
		const errors = [];

		templateFields.forEach(field => {
			const { name, required, type } = field;
			const value = formData.fields?.[name];

			if (required && (value === undefined || value === null || value === '')) {
				errors.push(`Field '${name}' is required`);
			}

			// Type-specific validation
			if (value !== undefined && value !== null) {
				switch (type) {
					case FIELDS_START_END_INPUTS:
						if (typeof value !== 'object' || !value.start || !value.end) {
							errors.push(`Field '${name}' must be a valid date range`);
						}
						break;
					case FIELD_TEXT:
					case FIELD_TEXTAREA:
						if (typeof value !== 'string') {
							errors.push(`Field '${name}' must be text`);
						}
						break;
					// Add more validations as needed
				}
			}
		});

		return {
			isValid: errors.length === 0,
			errors
		};
	}
}

export default FormHelper;